import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Minus, Plus, ShieldCheck, Truck, Zap } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { OptionSelector } from "../components/OptionSelector";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Skeleton } from "../components/ui/skeleton";
import { useCart } from "../context/CartContext";
import { api, fmt, track } from "../lib/api";
import { SITE } from "../config/site";

const RECENT_KEY = "ccdc_recent";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setDrawerOpen, verifyGate } = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [selection, setSelection] = useState({ options: {}, priceDelta: 0, display: [] });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    setLoading(true);
    setActiveImage(0);
    setQty(1);
    api.product(slug)
      .then((d) => {
        setData(d);
        track("view_item", { productId: d.product.id, name: d.product.name, price: d.product.price });
        try {
          const list = JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
          const next = [d.product, ...list.filter((p) => p.id !== d.product.id)].slice(0, 6);
          localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        } catch {}
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    try {
      setRecent(JSON.parse(localStorage.getItem(RECENT_KEY)) || []);
    } catch {}
  }, [slug]);

  const product = data?.product;
  const images = useMemo(() => {
    if (!product) return [];
    return [product.image, ...(product.gallery || [])].filter(Boolean);
  }, [product]);

  const unitPrice = product ? product.price + (selection.priceDelta || 0) : 0;

  const doAdd = (goCheckout) => {
    const withPrice = { ...product, price: unitPrice };
    addItem(withPrice, qty, selection.options, selection.display);
    if (goCheckout) {
      verifyGate(() => {
        track("begin_checkout", { source: "buy_now", productId: product.id });
        navigate("/checkout");
      });
    } else {
      setDrawerOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10" data-testid="product-loading">
        <Skeleton className="h-4 w-48" />
        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          <Skeleton className="aspect-square rounded-[2rem]" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-black text-brand-forest">Product not found</h1>
        <p className="mt-3 text-brand-ink/60">This item may have sold out or moved.</p>
        <Link to="/shop" data-testid="not-found-shop-link" className="mt-6 inline-block rounded-full bg-brand-pine px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream">
          Back to the Menu
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
      <SEO
        title={product.name}
        description={product.seoDescription || product.descriptionText?.slice(0, 150) || `${product.name} — available at CannaCheap DC with fast DC delivery.`}
        path={`/product/${product.slug}`}
        image={product.image}
        type="product"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product",
          name: product.name, image: images, sku: product.sku,
          description: product.descriptionText,
          offers: {
            "@type": "Offer", price: product.price, priceCurrency: "USD",
            availability: product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          },
        }}
      />

      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-brand-ink/50">
        <Link to="/" data-testid="breadcrumb-home" className="hover:text-brand-pine">Home</Link>
        <span>/</span>
        <Link to="/shop" data-testid="breadcrumb-shop" className="hover:text-brand-pine">Shop</Link>
        <span>/</span>
        <span className="text-brand-ink/80">{product.name}</span>
      </nav>

      <div className="mt-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <motion.div
            key={images[activeImage]}
            initial={{ opacity: 0.4, scale: 0.99 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden rounded-[2rem]"
          >
            {images.length > 0 ? (
              <img src={images[activeImage]} alt={product.name} className="img-blend aspect-square w-full object-contain p-4" data-testid="product-main-image" />
            ) : (
              <div className="flex aspect-square items-center justify-center rounded-[2rem] border border-brand-line bg-white font-display text-brand-ink/20">CannaCheap DC</div>
            )}
            <div className="absolute left-4 top-4 flex flex-col gap-2">
              {product.onSale && (
                <span data-testid="product-sale-badge" className="rounded-full bg-brand-gold px-4 py-1.5 text-[11px] font-display font-bold uppercase tracking-[0.15em] text-brand-forest">
                  Sale
                </span>
              )}
            </div>
          </motion.div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumbnail-${i}`}
                  onClick={() => setActiveImage(i)}
                  aria-label={`View image ${i + 1}`}
                  className={`h-20 w-20 overflow-hidden rounded-xl border-2 transition-colors ${i === activeImage ? "border-brand-gold" : "border-brand-line/60"}`}
                >
                  <img src={img} alt="" className="img-blend h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <Link to="/shop" data-testid="back-to-shop" className="mb-6 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/50 transition-colors hover:text-brand-pine">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to menu
          </Link>
          <h1 className="text-balance font-display text-4xl font-black leading-[1.02] text-brand-forest sm:text-5xl" data-testid="product-name">
            {product.name}
          </h1>

          <div className="mt-4 flex flex-wrap items-baseline gap-3">
            <span className="font-display text-4xl font-black text-brand-pine" data-testid="product-price">{fmt(unitPrice)}</span>
            {product.comparePrice && (
              <span className="text-xl text-brand-ink/40 line-through" data-testid="product-compare-price">{fmt(product.comparePrice)}</span>
            )}
            {product.sku && <span className="text-xs text-brand-ink/40">SKU {product.sku}</span>}
          </div>

          <p className={`mt-3 flex items-center gap-2 text-sm font-medium ${product.inStock ? "text-brand-moss" : "text-red-600"}`} data-testid="product-availability">
            <BadgeCheck className="h-4 w-4" />
            {product.inStock ? "In stock — ready for delivery or pickup" : "Currently out of stock"}
          </p>

          <div className="mt-8">
            <OptionSelector product={product} onChange={setSelection} />
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center rounded-full border border-brand-line bg-white">
              <button data-testid="qty-minus" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 px-4 text-brand-forest">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-8 text-center font-display text-lg font-black" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" aria-label="Increase quantity" onClick={() => setQty(qty + 1)} className="p-3 px-4 text-brand-forest">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              data-testid="add-to-cart"
              onClick={() => doAdd(false)}
              disabled={!product.inStock}
              className="flex-1 rounded-full border-2 border-brand-pine py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine transition-colors hover:bg-brand-pine hover:text-brand-cream disabled:opacity-40"
            >
              Add to Cart
            </button>
          </div>
          <button
            data-testid="buy-now"
            onClick={() => doAdd(true)}
            disabled={!product.inStock}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-brand-gold py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.01] disabled:opacity-40"
          >
            <Zap className="h-4 w-4" /> Buy Now
          </button>

          <div className="mt-8 grid grid-cols-2 gap-3 text-xs text-brand-ink/60">
            <p className="flex items-center gap-2 rounded-xl border border-brand-line bg-white px-4 py-3">
              <Truck className="h-4 w-4 shrink-0 text-brand-pine" /> 30–90 min DC delivery
            </p>
            <p className="flex items-center gap-2 rounded-xl border border-brand-line bg-white px-4 py-3">
              <ShieldCheck className="h-4 w-4 shrink-0 text-brand-pine" /> 21+ · ID verified at handoff
            </p>
          </div>

          <div className="mt-8">
            <Accordion type="single" collapsible defaultValue="details">
              <AccordionItem value="details" className="border-brand-line">
                <AccordionTrigger data-testid="accordion-details" className="font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest">
                  Product Details
                </AccordionTrigger>
                <AccordionContent>
                  <div className="prose-brand" dangerouslySetInnerHTML={{ __html: product.descriptionHtml || `<p>${product.descriptionText || "Premium quality, honestly priced."}</p>}` }} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="delivery" className="border-brand-line">
                <AccordionTrigger data-testid="accordion-delivery" className="font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest">
                  Delivery & Pickup
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-brand-ink/65">
                  Choose express pickup or same-day delivery at checkout. Delivery covers all of Washington DC
                  plus select Maryland and Northern Virginia neighborhoods — free on orders {fmt(SITE.freeDeliveryMinimum)}+.
                  Payments: {SITE.payments.join(", ")}.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {data.related?.length > 0 && (
        <section className="mt-20 lg:mt-28" data-testid="related-products">
          <Reveal>
            <h2 className="text-3xl font-black text-brand-forest sm:text-4xl">Pairs Well With</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4 lg:gap-x-6">
            {data.related.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {recent.filter((p) => p.id !== product.id).length > 0 && (
        <section className="mt-20" data-testid="recently-viewed">          <Reveal>
            <h2 className="text-2xl font-black text-brand-forest">Recently Viewed</h2>
          </Reveal>
          <div className="scrollbar-none -mx-4 mt-6 flex gap-5 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
            {recent.filter((p) => p.id !== product.id).slice(0, 5).map((p) => (
              <Link key={p.id} to={`/product/${p.slug}`} data-testid={`recent-${p.id}`} className="group w-40 shrink-0">
                <div className="aspect-square overflow-hidden rounded-xl">
                  {p.image && <img src={p.image} alt={p.name} loading="lazy" className="img-blend h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                </div>
                <p className="mt-2 truncate text-sm font-bold text-brand-ink">{p.name}</p>
                <p className="text-sm font-black text-brand-pine">{fmt(p.price)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {product.inStock && (
        <div data-testid="mobile-buy-bar" className="fixed inset-x-0 bottom-[60px] z-40 border-t border-brand-line bg-brand-cream/95 px-4 py-3 backdrop-blur-xl md:hidden">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-brand-ink/55">{product.name}</p>
              <p className="font-display text-lg font-black leading-tight text-brand-pine">{fmt(unitPrice)}</p>
            </div>
            <button
              data-testid="mobile-bar-add-to-cart"
              onClick={() => doAdd(false)}
              className="rounded-full border-2 border-brand-pine px-5 py-3 font-display text-xs font-bold uppercase tracking-wider text-brand-pine"
            >
              Add
            </button>
            <button
              data-testid="mobile-bar-buy-now"
              onClick={() => doAdd(true)}
              className="rounded-full bg-brand-gold px-5 py-3 font-display text-xs font-bold uppercase tracking-wider text-brand-forest"
            >
              Buy Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
